import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import {
  Input,
  Button,
  Table,
  Tag,
  Modal,
  message,
  Tooltip,
  Popconfirm,
  Spin,
  Collapse,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  LineChartOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import styles from "./PositionTrackerPage.module.scss";
import {
  getTrackedProducts,
  getTrackedProduct,
  createTrackedProduct,
  updateTrackedProduct,
  deleteTrackedProduct,
  startPositionCheck,
  updateQueryComment,
} from "../../../redux/actions/position-tracker/positionTracker";
import PositionHistoryChart from "../../../components/site/PositionTracker/PositionHistoryChart";

// Mirrors backend extract_sku (apps/position_tracker/utils.py).
const extractSku = (url) => {
  if (!url) return null;
  const cleaned = url.split("?")[0].replace(/\/+$/, "");
  const match = cleaned.match(/-(\d+)$/);
  if (match) return match[1];
  const lastSegment = cleaned.split("/").pop() || "";
  const digits = lastSegment.replace(/\D/g, "");
  return digits || null;
};

// Kaspi runs in Kazakhstan; positions are always shown in Almaty local time.
const ALMATY_TZ = "Asia/Almaty";

const formatAlmaty = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("ru-RU", {
    timeZone: ALMATY_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Most recent successful/attempted check across all of a product's queries.
const productLastCheckedAt = (product) => {
  const times = (product.queries || [])
    .map((q) => q.latest_check?.checked_at)
    .filter(Boolean)
    .map((t) => new Date(t).getTime());
  return times.length ? Math.max(...times) : null;
};

// Inline, auto-saving comment field for a single tracked query.
// Debounces persistence (~800ms after typing stops) and flushes on blur.
const COMMENT_SAVE_DELAY = 800;

const CommentCell = ({ productId, query }) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(query.comment || "");
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  const timerRef = useRef(null);
  const lastSavedRef = useRef(query.comment || "");

  // Adopt server-side changes only when they don't clobber the user's edits.
  useEffect(() => {
    const incoming = query.comment || "";
    if (incoming !== lastSavedRef.current) {
      lastSavedRef.current = incoming;
      setValue(incoming);
    }
  }, [query.comment]);

  useEffect(
    () => () => timerRef.current && clearTimeout(timerRef.current),
    []
  );

  const saveNow = async (next) => {
    if (next === lastSavedRef.current) return;
    setStatus("saving");
    const result = await dispatch(
      updateQueryComment({ productId, queryId: query.id, comment: next })
    );
    if (updateQueryComment.fulfilled.match(result)) {
      lastSavedRef.current = next;
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 1500);
    } else {
      setStatus("idle");
      message.error("Не удалось сохранить комментарий.");
    }
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveNow(next), COMMENT_SAVE_DELAY);
  };

  const handleBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    saveNow(value);
  };

  return (
    <div className={styles.commentCell}>
      <Input.TextArea
        autoSize={{ minRows: 1, maxRows: 4 }}
        placeholder="Комментарий…"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <span className={styles.commentStatus}>
        {status === "saving"
          ? "Сохранение…"
          : status === "saved"
          ? "Сохранено"
          : ""}
      </span>
    </div>
  );
};

const QueryEditor = ({ value, onChange }) => (
  <div className={styles.queryEditor}>
    {value.map((q, i) => (
      <div key={i} className={styles.queryRow}>
        <Input
          placeholder="Поисковый запрос, напр. футболка"
          value={q}
          onChange={(e) => {
            const next = [...value];
            next[i] = e.target.value;
            onChange(next);
          }}
        />
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => onChange(value.filter((_, idx) => idx !== i))}
        />
      </div>
    ))}
    <Button
      type="dashed"
      icon={<PlusOutlined />}
      onClick={() => onChange([...value, ""])}
    >
      Добавить запрос
    </Button>
  </div>
);

const PositionTrackerPage = () => {
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.positionTracker);
  const profile = useSelector((state) => state.user.userProfile.data);
  const isAdmin = !!profile?.is_admin;

  // New product form
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [queries, setQueries] = useState([""]);
  const [saving, setSaving] = useState(false);

  // History modal
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyQuery, setHistoryQuery] = useState(null);

  // Edit-queries modal
  const [editProduct, setEditProduct] = useState(null);
  const [editQueries, setEditQueries] = useState([]);

  const [checkingIds, setCheckingIds] = useState([]);
  const pollRef = useRef(null);

  const sku = extractSku(url);

  useEffect(() => {
    if (isAdmin) {
      dispatch(getTrackedProducts());
    }
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [dispatch, isAdmin]);

  const handleSave = async () => {
    if (!sku) {
      message.error("Не удалось определить SKU из ссылки.");
      return;
    }
    const cleanQueries = queries.map((q) => q.trim()).filter(Boolean);
    if (!cleanQueries.length) {
      message.error("Добавьте хотя бы один поисковый запрос.");
      return;
    }
    setSaving(true);
    const result = await dispatch(
      createTrackedProduct({ url, title: title || null, queries: cleanQueries })
    );
    setSaving(false);
    if (createTrackedProduct.fulfilled.match(result)) {
      message.success("Товар добавлен.");
      setUrl("");
      setTitle("");
      setQueries([""]);
    } else {
      message.error(result.payload?.error || "Ошибка при добавлении.");
    }
  };

  const pollResults = () => {
    let ticks = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      ticks += 1;
      dispatch(getTrackedProducts());
      if (ticks >= 12) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setCheckingIds([]);
      }
    }, 5000);
  };

  const handleCheck = async (productId) => {
    setCheckingIds((ids) => [...ids, productId]);
    const result = await dispatch(startPositionCheck(productId));
    if (startPositionCheck.fulfilled.match(result)) {
      message.info("Проверка позиций запущена. Результаты появятся скоро.");
      pollResults();
    } else {
      message.error(result.payload?.error || "Не удалось запустить проверку.");
      setCheckingIds((ids) => ids.filter((id) => id !== productId));
    }
  };

  const handleDelete = async (productId) => {
    const result = await dispatch(deleteTrackedProduct(productId));
    if (deleteTrackedProduct.fulfilled.match(result)) {
      message.success("Товар удалён.");
    }
  };

  const openHistory = async (productId, query) => {
    setHistoryQuery({ ...query, history: [] });
    setHistoryOpen(true);
    setHistoryLoading(true);
    const result = await dispatch(getTrackedProduct(productId));
    setHistoryLoading(false);
    if (getTrackedProduct.fulfilled.match(result)) {
      const fresh = result.payload.queries.find((q) => q.id === query.id);
      setHistoryQuery(fresh || { ...query, history: [] });
    }
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setEditQueries(product.queries.map((q) => q.text));
  };

  const saveEdit = async () => {
    const cleanQueries = editQueries.map((q) => q.trim()).filter(Boolean);
    const result = await dispatch(
      updateTrackedProduct({
        id: editProduct.id,
        data: { queries: cleanQueries },
      })
    );
    if (updateTrackedProduct.fulfilled.match(result)) {
      message.success("Запросы обновлены.");
      setEditProduct(null);
    } else {
      message.error("Не удалось обновить запросы.");
    }
  };

  const renderLatest = (q) => {
    const c = q.latest_check;
    if (!c) return <Tag>Не проверялось</Tag>;
    if (c.status === "success") {
      return (
        <Tag color="green">
          #{c.position} (стр. {c.page})
        </Tag>
      );
    }
    if (c.status === "not_found") {
      return <Tag color="orange">Не найдено (10+ стр.)</Tag>;
    }
    return <Tag color="red">Ошибка</Tag>;
  };

  const queryColumns = (product) => [
    {
      title: "Запрос",
      dataIndex: "text",
      key: "text",
    },
    {
      title: "Комментарии",
      key: "comment",
      width: 260,
      render: (_, q) => <CommentCell productId={product.id} query={q} />,
    },
    {
      title: "Позиция",
      key: "position",
      render: (_, q) => renderLatest(q),
    },
    {
      title: "Последняя проверка",
      key: "checked_at",
      render: (_, q) =>
        q.latest_check ? formatAlmaty(q.latest_check.checked_at) : "—",
    },
    {
      title: "",
      key: "actions",
      render: (_, q) => (
        <Tooltip title="История позиций">
          <Button
            size="small"
            icon={<LineChartOutlined />}
            onClick={() => openHistory(product.id, q)}
          />
        </Tooltip>
      ),
    },
  ];

  if (!isAdmin) {
    return <div className={styles.noAccess}>Нет доступа.</div>;
  }

  return (
    <div className={styles.wrap}>
      <Helmet>
        <title>Позиции в поиске — SellStat</title>
      </Helmet>

      <h1 className={styles.heading}>Позиции в поиске Kaspi</h1>
      <p className={styles.subheading}>
        Отслеживайте, на каком месте ваш товар появляется в результатах поиска
        Kaspi по нужным запросам (Алматы).
      </p>

      <div className={styles.formCard}>
        <label className={styles.label}>Ссылка на товар Kaspi</label>
        <Input
          size="large"
          prefix={<SearchOutlined />}
          placeholder="https://kaspi.kz/shop/p/...-114591936/"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        {url && (
          <div className={styles.skuHint}>
            {sku ? (
              <>
                SKU: <b>{sku}</b>
              </>
            ) : (
              <span className={styles.skuError}>
                Не удалось определить SKU из ссылки
              </span>
            )}
          </div>
        )}

        <label className={styles.label}>Название (необязательно)</label>
        <Input
          placeholder="Напр. Новогодний венок"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <label className={styles.label}>Поисковые запросы</label>
        <QueryEditor value={queries} onChange={setQueries} />

        <Button
          type="primary"
          size="large"
          loading={saving}
          onClick={handleSave}
          className={styles.saveBtn}
        >
          Сохранить товар
        </Button>
      </div>

      <h2 className={styles.heading2}>Отслеживаемые товары</h2>
      {products.loading && !products.data.length ? (
        <Spin />
      ) : (
        products.data.map((product) => (
          <div key={product.id} className={styles.productCard}>
            <div className={styles.productHeader}>
              <div>
                <a
                  href={product.product_url}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.productTitle}
                >
                  {product.title || `SKU ${product.sku}`}
                </a>
                <span className={styles.productSku}>SKU: {product.sku}</span>
              </div>
              <div className={styles.productActions}>
                <Button onClick={() => openEdit(product)}>
                  Изменить запросы
                </Button>
                {(() => {
                  const last = productLastCheckedAt(product);
                  const checking = checkingIds.includes(product.id);
                  return (
                    <Tooltip title="Нажмите, чтобы проверить позиции сейчас">
                      <span
                        className={styles.lastCheck}
                        onClick={() => !checking && handleCheck(product.id)}
                      >
                        {checking ? (
                          <>
                            <Spin size="small" /> Проверяем…
                          </>
                        ) : last ? (
                          <>Последняя проверка была {formatAlmaty(last)} (Алматы)</>
                        ) : (
                          <>Ещё не проверялось — нажмите, чтобы проверить</>
                        )}
                      </span>
                    </Tooltip>
                  );
                })()}
                <Popconfirm
                  title="Удалить товар?"
                  onConfirm={() => handleDelete(product.id)}
                  okText="Да"
                  cancelText="Нет"
                >
                  <Button danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </div>
            <Collapse
              ghost
              items={[
                {
                  key: "queries",
                  label: `Поисковые запросы (${product.queries.length})`,
                  children: (
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      columns={queryColumns(product)}
                      dataSource={product.queries}
                    />
                  ),
                },
              ]}
            />
          </div>
        ))
      )}

      <Modal
        title={`История позиций: ${historyQuery?.text || ""}`}
        open={historyOpen}
        footer={null}
        width={640}
        onCancel={() => setHistoryOpen(false)}
      >
        {historyLoading ? (
          <div className="tw-text-center tw-py-8">
            <Spin />
          </div>
        ) : (
          <PositionHistoryChart history={historyQuery?.history || []} />
        )}
      </Modal>

      <Modal
        title="Изменить запросы"
        open={!!editProduct}
        onOk={saveEdit}
        okText="Сохранить"
        cancelText="Отмена"
        onCancel={() => setEditProduct(null)}
      >
        <QueryEditor value={editQueries} onChange={setEditQueries} />
      </Modal>
    </div>
  );
};

export default PositionTrackerPage;
